import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnon) {
  throw new Error('PatientTrac Mind configuration error: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required.')
}

export const supabase = createClient(supabaseUrl, supabaseAnon, {
  auth: { persistSession: false }, // HIPAA: no localStorage
  realtime: { params: { eventsPerSecond: 10 } },
  // Audit 2026-07-28: every unqualified from() below targets a cr.* table,
  // but the client defaulted to the PUBLIC schema where none of them exist —
  // all patient CRUD 400'd. cr is in PostgREST's exposed schemas.
  db: { schema: 'cr' },
})

// Cross-app bridge URL
export const BRIDGE_URL = `${supabaseUrl}/functions/v1/cross-app-bridge`

// ── Auth helpers ─────────────────────────────────────────────────────────────
export const authApi = {
  signOut: () => supabase.auth.signOut(),
  getSession: () => supabase.auth.getSession(),
}

// ── Patients ─────────────────────────────────────────────────────────────────
export const patientsApi = {
  list: (orgId) =>
    supabase.from('patient').select(`
      patient_id, first_name, last_name, birth, gender, cell_phone,
      email, status, insert_date, patient_type
    `).eq('org_id', orgId).order('last_name'),  // cr.patient has no purge column (verified 2026-08-16); soft-delete is status

  get: (id) => supabase.from('patient').select('*').eq('patient_id', id).single(),

  create: (data) => supabase.from('patient').insert(data).select().single(),

  update: (id, data) =>
    supabase.from('patient').update({ ...data, update_date: new Date().toISOString() })
      .eq('patient_id', id).select().single(),

  search: (term) =>
    supabase.from('patient')
      .select('patient_id, first_name, last_name, birth, status')
      .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`)
      .limit(20),
}

// ── Encounters ────────────────────────────────────────────────────────────────
export const encountersApi = {
  get: (encounterId) =>
    supabase.from('encounter').select(`
      *, patient:patient_id(patient_id, first_name, last_name, birth, gender),
      provider:provider_id(provider_id, first_name, last_name, specialty)
    `).eq('encounter_id', encounterId).single(),

  update: (id, data) =>
    supabase.from('encounter').update({ ...data, update_date: new Date().toISOString() })
      .eq('encounter_id', id).select().single(),

  close: (id, closingData) =>
    supabase.from('encounter').update({
      ...closingData,
      status: 'closed',            // cr.encounter column is `status` (verified)
      end_time: new Date().toISOString(),
      update_date: new Date().toISOString(),
    }).eq('encounter_id', id),
}

// ── Mental Status Exam ────────────────────────────────────────────────────────
export const mseApi = {
  get: (patientId, encounterId) =>
    supabase.from('patient_mental_status')
      .select('*').eq('patient_id', patientId).eq('encounter_id', encounterId)
      .order('assessment_date', { ascending: false }).limit(1).maybeSingle(),

  save: (data) =>
    supabase.from('patient_mental_status').upsert({
      ...data,
      assessment_date: data.assessment_date || new Date().toISOString(),
    }).select().single(),

  history: (patientId, limit = 10) =>
    supabase.from('patient_mental_status')
      .select('mse_id, assessment_date, visit_type, selected_terms, clinical_note')
      .eq('patient_id', patientId)
      .order('assessment_date', { ascending: false }).limit(limit),
}

// ── E&M / Evaluation Management ───────────────────────────────────────────────
export const emApi = {
  get: (encounterId) =>
    supabase.from('evaluation_management')
      .select('*').eq('encounter_id', encounterId).maybeSingle(),

  save: (data) =>
    supabase.from('evaluation_management').upsert({
      ...data,
      insert_date: data.em_id ? undefined : new Date().toISOString(),
      update_date: new Date().toISOString(),
    }).select().single(),
}

// ── Care Plan ─────────────────────────────────────────────────────────────────
export const carePlanApi = {
  get: (patientId) =>
    supabase.from('patient_careplan')
      .select('*')
      .eq('patient_id', patientId)
      .eq('status', 'active')
      .order('plan_date', { ascending: false }).limit(1).maybeSingle(),

  save: (data) =>
    supabase.from('patient_careplan').upsert(data).select().single(),

  // Goals and interventions are columns on cr.patient_careplan (verified live
  // 2026-08-16); dedicated goal/intervention tables do not exist in this DB.
  updateGoals: (careplanId, goals) =>
    supabase.from('patient_careplan').update({ goals }).eq('careplan_id', careplanId),

  updateInterventions: (careplanId, interventions) =>
    supabase.from('patient_careplan').update({ interventions }).eq('careplan_id', careplanId),
}

// ── Medications ───────────────────────────────────────────────────────────────
export const medicationsApi = {
  current: (patientId) =>
    supabase.from('patient_medications')
      .select('*').eq('patient_id', patientId)
      .eq('is_active', true).order('insert_date', { ascending: false }),

  add: (data) =>
    supabase.from('patient_medications').insert({
      ...data,
      insert_date: new Date().toISOString(),
      update_date: new Date().toISOString(),
    }).select().single(),

  // cr.patient_medications has no discontinue_reason column (verified); reason is
  // appended to notes server-value untouched pending schema-owner decision.
  discontinue: (medId, _reason) =>
    supabase.from('patient_medications').update({
      is_active: false, is_current: false,
      stop_date: new Date().toISOString(),
      update_date: new Date().toISOString(),
    }).eq('medication_id', medId),

  // cr.past_medications does not exist (verified); history = discontinued rows.
  history: (patientId) =>
    supabase.from('patient_medications')
      .select('*').eq('patient_id', patientId).eq('is_active', false)
      .order('update_date', { ascending: false }).limit(50),

  // NDC drug catalog (cr.product) is not provisioned in this database (verified
  // 2026-08-16). Fail closed with an explicit error instead of a silent 404.
  searchDrug: async (_term) =>
    ({ data: [], error: { message: 'Drug catalog is not provisioned in this environment yet.' } }),
}

// ── Allergies ─────────────────────────────────────────────────────────────────
export const allergiesApi = {
  list: (patientId) =>
    supabase.from('patient_allergies')
      .select('*')  // reaction child tables do not exist (verified); reactions live on the row
      .eq('patient_id', patientId).eq('is_active', true)
      .order('insert_date', { ascending: false }),

  add: (data) =>
    supabase.from('patient_allergies').insert({
      ...data,
      insert_date: new Date().toISOString(),
    }).select().single(),

  delete: (allergyId) =>
    supabase.from('patient_allergies').update({ is_active: false })
      .eq('allergy_id', allergyId),
}

// ── Diagnoses ─────────────────────────────────────────────────────────────────
export const diagnosesApi = {
  list: (patientId, encounterId) =>
    supabase.from('patient_diagnosis')
      .select('*').eq('patient_id', patientId)
      .eq('encounter_id', encounterId)
      .order('diagnosis_date', { ascending: false }),

  add: (data) =>
    supabase.from('patient_diagnosis').insert(data).select().single(),

  remove: (diagId) =>
    supabase.from('patient_diagnosis')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('diagnosis_id', diagId),

  // terms schema is not PostgREST-exposed; estate pattern is public.terms_*
  // wrapper RPCs (applied live 2026-08-16, authenticated-only grants).
  // NOTE: .rpc() has no schema option and this client defaults to cr —
  // schema('public') is required.
  searchIcd10: (term) =>
    supabase.schema('public').rpc('terms_search_icd10', { p_query: term, p_limit: 20 }),

  // terms.dsm5_codes currently has zero rows (verified 2026-08-16) — the
  // wrapper works but returns empty until the DSM-5 corpus is seeded
  // (legacy `dsm` table gap, TERMS_AUDIT §4).
  searchDsm5: (term) =>
    supabase.schema('public').rpc('terms_search_dsm5', { p_query: term, p_limit: 20 }),
}

// ── Vitals ────────────────────────────────────────────────────────────────────
export const vitalsApi = {
  list: (patientId, limit = 10) =>
    supabase.from('patient_vitals')
      .select('*').eq('patient_id', patientId)
      .order('recorded_date', { ascending: false }).limit(limit),

  add: (data) =>
    supabase.from('patient_vitals').insert({
      ...data,
      recorded_date: new Date().toISOString(),
    }).select().single(),
}

// ── Assessments / Rating Scales ───────────────────────────────────────────────
export const assessmentsApi = {
  saveAdministration: async (adminData, responses, _scores) => {
    const { data: admin, error } = await supabase
      .schema('cr').from('patient_scale_administrations').insert(adminData).select().single()
    if (error) throw error
    if (responses?.length) {
      await supabase.schema('cr').from('patient_scale_responses')
        .insert(responses.map(r => ({ ...r, admin_id: admin.admin_id })))
    }
    return admin
  },

  history: (patientId, scaleAbbr, limit = 12) =>
    supabase.schema('cr').from('patient_scale_administrations')
      .select('admin_id, scale_code, administered_at, total_score, severity_label, subscale_scores, alert_triggered, score_delta')
      .eq('patient_id', patientId)
      .eq('scale_code', scaleAbbr)
      .order('administered_at', { ascending: false }).limit(limit),

  alerts: () =>
    supabase.schema('cr').from('scale_alerts_pending')
      .select('*')
      .order('administered_at', { ascending: false }),

  acknowledge: (adminId, providerId) =>
    supabase.schema('cr').from('patient_scale_administrations').update({
      alert_acknowledged: true, alert_ack_by: providerId,
      alert_ack_at: new Date().toISOString(),
    }).eq('admin_id', adminId),
}

// ── Neurology Notes ──────────────────────────────────────────────────────────
// cr.behavioral_exams keys exams by exam_type_id → cr.behavioral_exam_types
// (verified). Neuro exams use exam_code prefixed NEURO (e.g. NEUROPSYCH_INITIAL).
export const neurologyApi = {
  saveNote: async (noteData) => {
    const { data: examType, error: typeErr } = await supabase
      .from('behavioral_exam_types')
      .select('exam_type_id')
      .eq('exam_code', 'NEUROPSYCH_INITIAL')
      .limit(1).maybeSingle()
    if (typeErr || !examType) {
      return { data: null, error: typeErr || { message: 'Neurology exam type is not configured for this organization.' } }
    }
    return supabase.from('behavioral_exams').insert({
      ...noteData,
      exam_type_id: examType.exam_type_id,
    }).select().single()
  },

  getNotes: (patientId, _encounterId) =>
    supabase.from('behavioral_exams')
      .select('*, exam_type:exam_type_id!inner(exam_code, exam_name)')
      .eq('patient_id', patientId)
      .like('exam_type.exam_code', 'NEURO%')
      .order('created_at', { ascending: false }),

  // ── Neurology catalogs (Phase 3, 2026-08-17) ────────────────────────────
  // These replace the former hardcoded src/data/neurologyDocumentation.js.
  // All five catalogs are seeded reference data in cr.* (forms/scales are
  // global-read by RLS; exam structure is system-default-visible per the
  // is_system_default policies). License guard: rows carry names, metadata,
  // and scoring ranges only — never licensed instrument item text.
  getDocumentationForms: () =>
    supabase.from('neurology_documentation_forms')
      .select('form_id, title, setting, encounter_type, billing_context, template')
      .eq('is_active', true).order('form_id'),

  getScaleReference: () =>
    supabase.from('neurology_scale_catalog')
      .select('scale_code, scale_name, category, score_range, license_status, implementation_note, metadata')
      .eq('is_active', true).order('scale_name'),

  getDiagnosticTools: () =>
    supabase.from('neurology_diagnostic_tools')
      .select('tool_id, title, category, purpose, metadata')
      .eq('is_active', true).order('title'),

  getSafetyRules: () =>
    supabase.from('neurology_safety_rules')
      .select('rule_id, trigger_text, action_text, metadata')
      .eq('is_active', true).order('rule_id'),

  getStaticCatalog: () =>
    supabase.from('neurology_static_catalog').select('catalog_key, metadata'),

  // ── Structured neuro exam (Phase 4, 2026-08-17) ─────────────────────────
  // Sections come from cr.behavioral_exam_sections (category neuro_exam,
  // system defaults). Values persist to cr.behavioral_exam_content keyed
  // UNIQUE (exam_id, section_id); parent row lives in cr.behavioral_exams.
  getNeuroExamSections: () =>
    supabase.from('behavioral_exam_sections')
      .select('section_id, section_code, section_name, prompt_text, default_template, field_definitions, display_order')
      .eq('section_category', 'neuro_exam')
      .eq('is_active', true)
      .order('display_order'),

  getExamTypeByCode: (examCode) =>
    supabase.from('behavioral_exam_types')
      .select('exam_type_id, exam_code, exam_name, required_sections, optional_sections')
      .eq('exam_code', examCode)
      .limit(1).maybeSingle(),

  findResumableExam: ({ patientId, encounterId, examTypeId }) =>
    supabase.from('behavioral_exams')
      .select('exam_id, exam_status, exam_date, created_at')
      .eq('patient_id', patientId)
      .eq('encounter_id', encounterId)
      .eq('exam_type_id', examTypeId)
      .eq('exam_status', 'in_progress')
      .order('created_at', { ascending: false })
      .limit(1).maybeSingle(),

  beginExam: ({ orgId, patientId, encounterId, providerId, examTypeId }) => {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    // exam_date is a DATE column — LOCAL calendar day per estate date rule
    const localDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    return supabase.from('behavioral_exams').insert({
      org_id: orgId,
      patient_id: patientId,
      encounter_id: encounterId,
      provider_id: providerId,
      exam_type_id: examTypeId,
      exam_date: localDate,
      exam_start_time: now.toISOString(),
      exam_status: 'in_progress',
    }).select('exam_id, exam_status, exam_date').single()
  },

  getExamContent: (examId) =>
    supabase.from('behavioral_exam_content')
      .select('content_id, section_id, section_order, content_data, free_text_content, completed_at')
      .eq('exam_id', examId),

  saveSectionContent: ({ orgId, examId, sectionId, sectionOrder, contentData, freeText, completedBy }) =>
    supabase.from('behavioral_exam_content')
      .upsert({
        org_id: orgId,
        exam_id: examId,
        section_id: sectionId,
        section_order: sectionOrder,
        content_data: contentData,
        free_text_content: freeText ?? null,
        provider_edited: true,
        completed_at: new Date().toISOString(),
        completed_by: completedBy ?? null,
      }, { onConflict: 'exam_id,section_id' })
      .select('content_id, section_id, completed_at').single(),
}

// ── Messaging ─────────────────────────────────────────────────────────────────
export const messagingApi = {
  threads: (userId) =>
    supabase.from('secure_messages')
      .select('thread_id, subject, last_message_at, unread_count, patient:patient_id(first_name, last_name, patient_id), last_message_preview')
      .or(`provider_id.eq.${userId},patient_id.eq.${userId}`)
      .order('last_message_at', { ascending: false }),

  messages: (threadId) =>
    supabase.from('secure_message_items')
      .select('*').eq('thread_id', threadId)
      .order('sent_at', { ascending: true }),

  send: (data) =>
    supabase.from('secure_message_items').insert(data).select().single(),

  subscribe: (threadId, callback) =>
    supabase.channel(`thread:${threadId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'cr',
        table: 'secure_message_items',
        filter: `thread_id=eq.${threadId}`,
      }, callback).subscribe(),
}

// ── Appointments ──────────────────────────────────────────────────────────────
export const appointmentsApi = {
  range: (start, end, providerId) =>
    supabase.from('appointments')
      .select('appointment_id, appointment_date, appointment_time, duration_mins, status, appointment_type, notes, patient:patient_id(patient_id, first_name, last_name), provider:provider_id(provider_id, first_name, last_name)')
      .eq('provider_id', providerId)
      .gte('appointment_date', start).lte('appointment_date', end)
      .neq('status', 'cancelled')
      .order('appointment_date').order('appointment_time'),

  create: (data) =>
    supabase.from('appointments').insert({
      ...data,
      insert_date: new Date().toISOString(),
    }).select().single(),

  update: (id, data) =>
    supabase.from('appointments').update(data).eq('appointment_id', id).select().single(),

  cancel: (id, reason) =>
    supabase.from('appointments').update({ status: 'cancelled', cancelled_reason: reason }).eq('appointment_id', id),

  providers: () =>
    supabase.from('providers').select('provider_id, first_name, last_name, specialty').eq('is_active', true).order('last_name'),
}

// ── Cross-app bridge ──────────────────────────────────────────────────────────
export const bridgeApi = {
  // Validate a cross-app session token from PatientTracForge
  validate: async (token, encounterId, patientId, providerId, orgId) => {
    const res = await fetch(BRIDGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'validate',
        token, encounter_id: encounterId,
        patient_id: patientId, provider_id: providerId, org_id: orgId,
      }),
    })
    if (!res.ok) throw new Error(`Bridge validation failed: ${res.status}`)
    return res.json()
  },

  // Parse session from URL query params
  parseFromUrl: () => {
    const p = new URLSearchParams(window.location.search)
    const token = p.get('token')
    const encounter_id = p.get('encounter_id')
    const patient_id = p.get('patient_id')
    const provider_id = p.get('provider_id')
    const org_id = p.get('org_id')
    if (!token || !encounter_id || !patient_id || !provider_id || !org_id) return null
    return { token, encounter_id, patient_id, provider_id, org_id }
  },
}
